import { useEffect, useState } from 'react';

interface Room {
  id: number;
  tenant_id: number;
  room_number: string;
  room_name: string;
  description: string;
  pin: string;
  admin_pin: string;
  max_members: number;
  record_calls: boolean;
  require_pin: boolean;
  is_active: boolean;
  bridge_profile: string;
  created_at: string;
}

const EMPTY_ROOM: Omit<Room, 'id' | 'created_at'> = {
  tenant_id: 1,
  room_number: '',
  room_name: '',
  description: '',
  pin: '',
  admin_pin: '',
  max_members: 20,
  record_calls: false,
  require_pin: false,
  is_active: true,
  bridge_profile: 'ce_bridge_open',
};

export default function ConferenceRoomsPage({ showTenantSelector }: { showTenantSelector?: boolean }) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Room | null>(null);
  const [form, setForm] = useState<typeof EMPTY_ROOM>({ ...EMPTY_ROOM });
  const [saving, setSaving] = useState(false);
  const [tenantId, setTenantId] = useState(1);
  const [deleting, setDeleting] = useState<number | null>(null);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  async function load() {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`/api/conferences/rooms?tenant_id=${tenantId}`, { headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to load');
      setRooms(d);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [tenantId]);

  function openNew() {
    setEditing(null);
    setForm({ ...EMPTY_ROOM, tenant_id: tenantId });
    setShowModal(true);
  }

  function openEdit(r: Room) {
    setEditing(r);
    setForm({
      tenant_id: r.tenant_id,
      room_number: r.room_number,
      room_name: r.room_name,
      description: r.description || '',
      pin: r.pin || '',
      admin_pin: r.admin_pin || '',
      max_members: r.max_members,
      record_calls: r.record_calls,
      require_pin: r.require_pin,
      is_active: r.is_active,
      bridge_profile: r.bridge_profile,
    });
    setShowModal(true);
  }

  async function save() {
    setSaving(true);
    try {
      const body = {
        ...form,
        pin: form.pin || null,
        admin_pin: form.admin_pin || null,
      };
      const url = editing ? `/api/conferences/rooms/${editing.id}` : '/api/conferences/rooms';
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Save failed');
      setShowModal(false);
      load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteRoom(id: number) {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Delete this conference room?')) return;
    setDeleting(id);
    try {
      const r = await fetch(`/api/conferences/rooms/${id}`, { method: 'DELETE', headers });
      if (!r.ok) throw new Error('Delete failed');
      load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setDeleting(null);
    }
  }

  async function toggleActive(room: Room) {
    try {
      await fetch(`/api/conferences/rooms/${room.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ ...room, is_active: !room.is_active }),
      });
      load();
    } catch {}
  }

  return (
    <div style={{ padding: '28px 32px', color: '#e2e8f0', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>
            Conference Rooms
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Audio conference bridges — dial 8XXX to join. Managed in PostgreSQL.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {showTenantSelector && (
            <select
              value={tenantId}
              onChange={e => setTenantId(Number(e.target.value))}
              style={{ background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0',
                padding: '6px 12px', borderRadius: 7, fontSize: 13 }}
            >
              <option value={1}>Tenant 1</option>
              <option value={2}>Tenant 2</option>
            </select>
          )}
          <button onClick={load}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
              padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
            ↻ Refresh
          </button>
          <button onClick={openNew}
            style={{ background: '#C9A84C', border: 'none', color: '#0f172a',
              padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            + New Room
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div style={{ background: 'rgba(14,165,233,.08)', border: '1px solid rgba(14,165,233,.2)',
        borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#7dd3fc' }}>
        <strong>How it works:</strong> Dial an 8XXX number from any extension to join that conference bridge.
        Use <strong>*</strong> to mute/unmute. Rooms with a PIN will prompt before entry.
        Rooms are stored in PostgreSQL — no manual config file editing required.
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid #ef4444',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#fca5a5', fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: 60, fontSize: 15 }}>Loading rooms…</div>
      ) : rooms.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎙</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No conference rooms yet</div>
          <div style={{ fontSize: 13 }}>Click "New Room" to create your first conference bridge</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 16 }}>
          {rooms.map(room => (
            <div key={room.id}
              style={{ background: '#1e293b', border: `1px solid ${room.is_active ? '#334155' : '#1e293b'}`,
                borderRadius: 12, padding: 20, opacity: room.is_active ? 1 : 0.55, position: 'relative' }}>
              {/* Room number badge */}
              <div style={{ position: 'absolute', top: 14, right: 16, background: '#C9A84C',
                color: '#0f172a', borderRadius: 8, padding: '3px 10px',
                fontSize: 18, fontWeight: 800, letterSpacing: 1 }} title="Dial this code from any phone to join">
                *{room.room_number}
              </div>

              <div style={{ marginRight: 60 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>
                  {room.room_name}
                </div>
                {room.description && (
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{room.description}</div>
                )}
              </div>

              {/* Stats row */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, marginBottom: 12 }}>
                <span style={{ background: '#0f172a', borderRadius: 6, padding: '3px 8px',
                  fontSize: 11.5, color: '#94a3b8' }}>
                  👥 Max {room.max_members}
                </span>
                {room.require_pin && (
                  <span style={{ background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)',
                    borderRadius: 6, padding: '3px 8px', fontSize: 11.5, color: '#fbbf24' }}>
                    🔒 PIN protected
                  </span>
                )}
                {room.record_calls && (
                  <span style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
                    borderRadius: 6, padding: '3px 8px', fontSize: 11.5, color: '#f87171' }}>
                    ⏺ Recording
                  </span>
                )}
                <span style={{ background: room.bridge_profile === 'ce_bridge' ? 'rgba(139,92,246,.1)' : 'rgba(16,185,129,.1)',
                  border: `1px solid ${room.bridge_profile === 'ce_bridge' ? 'rgba(139,92,246,.3)' : 'rgba(16,185,129,.3)'}`,
                  borderRadius: 6, padding: '3px 8px', fontSize: 11.5,
                  color: room.bridge_profile === 'ce_bridge' ? '#c4b5fd' : '#6ee7b7' }}>
                  {room.bridge_profile === 'ce_bridge' ? 'Admin-hosted' : 'Always open'}
                </span>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, borderTop: '1px solid #0f172a', paddingTop: 12 }}>
                <button onClick={() => openEdit(room)}
                  style={{ flex: 1, background: 'rgba(255,255,255,.04)', border: '1px solid #334155',
                    color: '#94a3b8', padding: '6px 0', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
                  ✏ Edit
                </button>
                <button onClick={() => toggleActive(room)}
                  style={{ flex: 1, background: room.is_active ? 'rgba(239,68,68,.08)' : 'rgba(16,185,129,.08)',
                    border: `1px solid ${room.is_active ? 'rgba(239,68,68,.2)' : 'rgba(16,185,129,.2)'}`,
                    color: room.is_active ? '#f87171' : '#6ee7b7',
                    padding: '6px 0', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
                  {room.is_active ? '⏸ Disable' : '▶ Enable'}
                </button>
                <button onClick={() => deleteRoom(room.id)}
                  disabled={deleting === room.id}
                  style={{ background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.15)',
                    color: '#f87171', padding: '6px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
                  {deleting === room.id ? '…' : '🗑'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 16,
            padding: 28, width: 480, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', color: '#f1f5f9', fontSize: 18, fontWeight: 700 }}>
              {editing ? 'Edit Conference Room' : 'New Conference Room'}
            </h3>

            <Field label="Room Number (8XXX) — callers dial * + number, e.g. *8001" required>
              <input value={form.room_number}
                onChange={e => setForm(f => ({ ...f, room_number: e.target.value }))}
                placeholder="e.g. 8001" maxLength={10}
                style={inputStyle} />
            </Field>

            <Field label="Room Name" required>
              <input value={form.room_name}
                onChange={e => setForm(f => ({ ...f, room_name: e.target.value }))}
                placeholder="e.g. Main Conference"
                style={inputStyle} />
            </Field>

            <Field label="Description">
              <input value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
                style={inputStyle} />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="User PIN (optional)">
                <input value={form.pin}
                  onChange={e => setForm(f => ({ ...f, pin: e.target.value, require_pin: !!e.target.value }))}
                  placeholder="Leave blank = no PIN"
                  style={inputStyle} />
              </Field>
              <Field label="Admin PIN (optional)">
                <input value={form.admin_pin}
                  onChange={e => setForm(f => ({ ...f, admin_pin: e.target.value }))}
                  placeholder="Admin-only PIN"
                  style={inputStyle} />
              </Field>
            </div>

            <Field label="Max Participants">
              <input type="number" value={form.max_members} min={2} max={100}
                onChange={e => setForm(f => ({ ...f, max_members: Number(e.target.value) }))}
                style={inputStyle} />
            </Field>

            <Field label="Bridge Type">
              <select value={form.bridge_profile}
                onChange={e => setForm(f => ({ ...f, bridge_profile: e.target.value }))}
                style={{ ...inputStyle, appearance: 'none' as any }}>
                <option value="ce_bridge_open">Always Open (no admin needed)</option>
                <option value="ce_bridge">Admin-Hosted (waits for admin to start)</option>
              </select>
            </Field>

            <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#94a3b8', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.require_pin}
                  onChange={e => setForm(f => ({ ...f, require_pin: e.target.checked }))}
                  style={{ accentColor: '#C9A84C' }} />
                Require PIN to join
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#94a3b8', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.record_calls}
                  onChange={e => setForm(f => ({ ...f, record_calls: e.target.checked }))}
                  style={{ accentColor: '#C9A84C' }} />
                Record calls
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#94a3b8', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  style={{ accentColor: '#C9A84C' }} />
                Active
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button onClick={() => setShowModal(false)}
                style={{ flex: 1, background: 'transparent', border: '1px solid #334155',
                  color: '#64748b', padding: '10px 0', borderRadius: 9, cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
              <button onClick={save} disabled={saving || !form.room_number || !form.room_name}
                style={{ flex: 2, background: '#C9A84C', border: 'none', color: '#0f172a',
                  padding: '10px 0', borderRadius: 9, cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 700, fontSize: 14, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Room'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 5, fontWeight: 600 }}>
        {label}{required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  color: '#e2e8f0', padding: '8px 12px', borderRadius: 8, fontSize: 14,
  boxSizing: 'border-box',
};
